// src/hooks/useAuth.js
import { useState, useEffect, useRef, createContext, useContext, useCallback, useMemo } from 'react';
import {
  onAuthStateChanged, signInWithPopup, signInWithEmailAndPassword,
  updatePassword, signOut, reauthenticateWithCredential, reauthenticateWithPopup,
  EmailAuthProvider, getMultiFactorResolver, multiFactor,
  TotpMultiFactorGenerator, GoogleAuthProvider, signInWithCredential, signInWithCustomToken, linkWithCredential, linkWithPopup, unlink
} from 'firebase/auth';

import { auth, googleProvider, microsoftProvider, ALLOWED_DOMAINS, API_BASE } from '../firebase/config';
import { supportSignOut } from '../utils/supportSignOut';
import { describeAuthError } from '../utils/authErrors';
import { lostUseRecoveryCode, lostEmailResetLink, lostApprovers, lostAskAdmin, rememberedSignIn, rememberThisDevice, signInEmailFor } from '../utils/api';
import { LOCK_KEY, lockFor, clearLock, currentLock, passkeyFor, forgetPasskey, renewPasskey, unlockToken, passkeySignInToken, savePasskey,
  isInstalledApp, passkeysPossible, offerDeclined } from '../utils/passkeys';

// 3.49.0: Security → IP Blocking. Firebase checks passwords and codes, so a
// failed one is reported to the server, which counts them per network
// address and blocks it after too many ({ blocked, until } comes back).
const FAIL_REASONS = {
  'auth/invalid-credential': 'password', 'auth/invalid-login-credentials': 'password', 'auth/wrong-password': 'password',
  'auth/user-not-found': 'password', 'auth/too-many-requests': 'too-many', 'auth/invalid-verification-code': 'code', 'auth/code-expired': 'code',
};
async function reportSignInFailure(email, e) {
  const reason = FAIL_REASONS[e?.code];
  if (!reason) return null;
  try {
    const res = await fetch(`${API_BASE}/portal/access/sign-in-failed`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, reason }) });
    return res.ok ? res.json() : null;
  } catch { return null; }
}
// The message a blocked network sees (3.49.2): why (failed sign-ins, too
// many requests, or a Super Admin's block), how long in words and until when
// (in their own time). b: { kind, from, until } from the server.
const lengthInWords = ms => {
  const min = Math.round(ms / 60000);
  if (min < 90) return `${min} minute${min === 1 ? '' : 's'}`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} hour${h === 1 ? '' : 's'}`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'}`;
};
export function ipBlockedMessage(b = {}) {
  const { kind, from, until } = b || {};
  if (!until) return 'Access to the Fax Portal from your network is blocked. Please contact Fax Portal support.';
  const end = new Date(until).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const span = from ? `for ${lengthInWords(Date.parse(until) - Date.parse(from))}, ` : '';
  if (kind === 'manual') return `Access to the Fax Portal from your network is blocked ${span}until ${end}. Please contact Fax Portal support.`;
  if (kind === 'spam') return `Too many requests came from your network, so the Fax Portal is paused ${span}until ${end}. Please try again then, or contact Fax Portal support.`;
  return `Too many failed sign-ins came from your network, so signing in is paused ${span}until ${end}. Please try again then, or contact Fax Portal support.`;
}

// How an account can sign in: people may have both a password and Google
// (3.72.0: and Microsoft, once connected in Security)
export function signInMethods(user) {
  const ids = (user?.providerData || []).map(p => p.providerId);
  return { password: ids.includes('password'), google: ids.includes('google.com'), microsoft: ids.includes('microsoft.com') };
}
// 3.72.0: the Microsoft account a portal account is connected to (its email)
export function microsoftAccountOf(user) {
  return (user?.providerData || []).find(p => p.providerId === 'microsoft.com')?.email || '';
}

// Remembered devices: after a sign-in with the code, a person can choose
// "Don't ask for a code on this device for 30 days". This browser keeps a
// device token per email; the password (or Google) is still needed each
// time. Kept across sign-outs; the server decides if it still counts.
const DEVICES_KEY = 'faxPortal.rememberedDevices';
function readDevices() {
  try { return JSON.parse(window.localStorage.getItem(DEVICES_KEY) || '{}') || {}; } catch { return {}; }
}
function writeDevices(map) {
  try { window.localStorage.setItem(DEVICES_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}
function rememberedDeviceFor(email) {
  const d = readDevices()[String(email || '').toLowerCase()];
  return d && d.token && Date.parse(d.expiresAt) > Date.now() ? d : null;
}
function forgetDeviceLocally(email) {
  const map = readDevices();
  delete map[String(email || '').toLowerCase()];
  writeDevices(map);
}
export function rememberedDeviceId(email) { return rememberedDeviceFor(email)?.id || null; }
// A short name for this browser, shown in "Remembered devices" (and, 3.69.0,
// Face ID / fingerprint unlock: the installed app says so)
export function deviceLabel() {
  const ua = navigator.userAgent || '';
  const browser = isInstalledApp() ? 'App' : /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /iPhone|iPad/.test(ua) ? 'iPhone/iPad' : /Mac OS/.test(ua) ? 'Mac' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}
// Email inside a Google ID token (for finding this browser's device token)
function emailFromIdToken(idToken) {
  try { return JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).email || ''; } catch { return ''; }
}

// Google's credential from a "2FA code needed" error (3.24.1). For that
// error Firebase keeps Google's tokens in customData._serverResponse, not
// _tokenResponse, so GoogleAuthProvider.credentialFromError() returns null
// and "Lost access to your authenticator app?" (and "Don't ask on this
// device") had no proof of the Google sign-in.
function googleCredentialFromMfaError(e) {
  const direct = GoogleAuthProvider.credentialFromError(e);
  if (direct?.idToken) return direct;
  const r = e?.customData?._serverResponse || e?.customData?._tokenResponse || {};
  if (!r.oauthIdToken && !r.oauthAccessToken) return direct;
  try { return GoogleAuthProvider.credential(r.oauthIdToken || null, r.oauthAccessToken || null); } catch { return direct; }
}

// 3.72.0: a Microsoft account that isn't connected to a portal account
const MS_NOT_CONNECTED = "This Microsoft account doesn't match a Fax Portal account. If it uses a different email from your work email, sign in with your email and password (or Google), then connect it in Security → Password.";

export const AuthContext = createContext(null);   // exported for the dev layout preview (src/dev)

// Session ID stored in memory for current session
let currentSessionId = null;

// The server answers 403 with a code when two-factor authentication must
// be set up (MFA_SETUP_REQUIRED) or the account no longer has access
// (NOT_AUTHORIZED). Watch every portal API call for those and tell the
// AuthProvider, so the whole app reacts in one place.
//
// It also adds "View as" (Super Admins: full access; Admins with users.viewAs:
// people below them in their account, read only): while using the portal
// as someone, portal API calls carry X-View-As so the server answers as
// that person. Calls about your own account never do (VIEW_AS_OWN_PATHS).
const VIEW_AS_KEY = 'faxPortal.viewAs';
// Permissions that change something (hidden while viewing read only)
// 3.62.16 (audit C2): + the other buttons that change something (★ priority,
// mark as new, contacts, Problem numbers marks, reminder schedules)
const WRITE_PERMS = ['fax.send', 'fax.resend', 'fax.delete', 'fax.cancelRetry', 'users.add', 'users.remove', 'users.changeRole',
  'users.password', 'users.reset2fa', 'users.online', 'reminders.send', 'users.unblock',
  'fax.priority', 'inbox.markNew', 'contacts.edit', 'contacts.remove', 'problemNumbers.mark', 'reminders.schedule'];
const VIEW_AS_OWN_PATHS = ['/portal/user-info', '/portal/users/view-as', '/portal/session/', '/portal/me', '/portal/mfa/'];
function readViewAs() {
  try { const v = JSON.parse(window.sessionStorage.getItem(VIEW_AS_KEY) || 'null'); return v && v.email ? v : null; } catch { return null; }
}
let viewAsPerson = typeof window !== 'undefined' ? readViewAs() : null;

// Accounts (3.0): the account picked in the header switcher goes on portal
// API calls as X-Account, so every tab shows that account alone. Super
// Admins also have "all". Nothing picked: the server uses their main
// account (Super Admins: all accounts). Kept in this browser until sign-out.
const ACCOUNT_KEY = 'faxPortal.account';
const ACCOUNT_SKIP_PATHS = ['/portal/session/'];
function readAccount() {
  try { return window.localStorage.getItem(ACCOUNT_KEY) || null; } catch { return null; }
}
let currentAccount = typeof window !== 'undefined' ? readAccount() : null;
function setCurrentAccount(id) {
  currentAccount = id || null;
  try {
    if (currentAccount) window.localStorage.setItem(ACCOUNT_KEY, currentAccount);
    else window.localStorage.removeItem(ACCOUNT_KEY);
  } catch { /* ignore */ }
}

if (typeof window !== 'undefined' && window.fetch && !window.__faxAccessWatch) {
  window.__faxAccessWatch = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (viewAsPerson && url.startsWith(API_BASE) && !VIEW_AS_OWN_PATHS.some(p => url.startsWith(API_BASE + p))) {
      const headers = new Headers((init && init.headers) || undefined);
      headers.set('X-View-As', viewAsPerson.email);
      init = { ...(init || {}), headers };
    }
    if (currentAccount && url.startsWith(API_BASE) && !ACCOUNT_SKIP_PATHS.some(p => url.startsWith(API_BASE + p))) {
      const headers = new Headers((init && init.headers) || undefined);
      if (!headers.has('X-Account')) headers.set('X-Account', currentAccount);
      init = { ...(init || {}), headers };
    }
    const res = await originalFetch(input, init);
    if (res.status === 403) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url.startsWith(API_BASE)) {
        res.clone().json()
          .then(j => { if (j && j.code) window.dispatchEvent(new CustomEvent('fax:access', { detail: j })); })
          .catch(() => {});
      }
    }
    return res;
  };
}

// Inactivity sign-out after an hour without use (ms); the countdown
// pop-up shows for the last 5 and stays until "Stay signed in" (moving the
// mouse doesn't dismiss it). Activity in any portal tab of this browser
// counts for all of them (shared through localStorage).
// 3.63.0 (owner): 1 hour (was 30 minutes), NIST SP 800-63B-4's AAL2
// inactivity limit; reopening the portal after that asks for the password
// again too (below). No overall daily limit: the owner chose never to sign
// out someone who is working (a daily cut-off was dropped before release).
export const INACTIVITY_TIMEOUT = 60 * 60 * 1000;
const IDLE_SIGN_OUT_MESSAGE = 'You were signed out after an hour without use. Please sign in again.';
const INACTIVITY_WARNING    = INACTIVITY_TIMEOUT - 5 * 60 * 1000;
const LAST_ACTIVITY_KEY     = 'faxPortal.lastActivity';
function sharedLastActivity() {
  try { return Number(window.localStorage.getItem(LAST_ACTIVITY_KEY)) || 0; } catch { return 0; }
}
// 3.69.0: when a sign-in with the password (or Google, the code or a
// remembered device) last happened in this page; a Face ID unlock isn't
// one. Right after it, this device's passkey is renewed, or the installed
// app asks whether to turn Face ID / fingerprint unlock on.
let fullSignInAt = 0;
function fullSignInDone() { fullSignInAt = Date.now(); clearLock(); }

// Heartbeat every 2 minutes: keeps "Online now" in User Management current,
// and a sign-out by an Admin reaches this browser within that time
const HEARTBEAT_INTERVAL    = 2 * 60 * 1000;

// ── Close stale session on page load ─────────────────────────
// If previous session exists in localStorage, close it via unload endpoint
const staleSessionId = localStorage.getItem('fax_session_id');
if (staleSessionId) {
  try {
    fetch(`${API_BASE}/portal/session/unload`, {
      method   : 'POST',
      keepalive: true,
      headers  : { 'Content-Type': 'application/json' },
      body     : JSON.stringify({ sessionId: staleSessionId })
    });
  } catch (e) {}
  localStorage.removeItem('fax_session_id');
  // Flag to create new session after page reload restores auth
  localStorage.setItem('fax_create_session', 'true');
}

// What /portal/user-info returns, as the app uses it
function infoFrom(data) {
  return {
    email: data.email || '', name: data.name || '', role: data.role || '', phone: data.phone || '', phoneExt: data.phoneExt || '', whatsapp: data.whatsapp || '',
    roleId: data.roleId || '', level: data.level || '', perms: data.perms || {},
    group: data.group || null, groupLabel: data.groupLabel || '',
    mustChangePassword: !!data.mustChangePassword,
    askProfile: !!data.askProfile,   // 3.62.26: the "Your profile" step at first sign-in
    accounts: data.accounts || [], accountId: data.accountId || null, accountStore: data.accountStore || null,
    teams: data.teams || [], defaultTeam: data.defaultTeam || null,   // their teams at the picked client (3.19.0)
    lines: data.lines || [],   // an individual client's numbers, first = default (3.19.0)
    clientTeams: data.clientTeams || [],   // every team at the picked client, for the Team filter (3.19.3)
    hipaa: !!data.hipaa,   // the picked client handles PHI under HIPAA safeguards (3.19.9)
    complianceReport: !!data.complianceReport,   // the client's Compliance report tick (3.49.21)
    // 3.64.0: Compose's starting cover sheet (Settings → Cover Sheet) and phone, which
    // were sent but left out here until now, and the client's Send Confirmation
    coverDefaults: data.coverDefaults || null, coverPhone: data.coverPhone || '', sendConfirm: !!data.sendConfirm,
    unassignedNumbers: data.unassignedNumbers || 0,
    avatar: data.avatar || null,   // profile picture (3.22.2)   // Super Admins: numbers no client has that got faxes (3.21.1)
  };
}


export function AuthProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [userInfo, setUserInfo]       = useState({ name: '', role: '' });
  const [loading, setLoading]         = useState(true);
  // Between a successful sign-in step and the portal opening (account
  // details still loading): the app shows the loading screen instead of
  // flashing the empty sign-in form. Cleared when the user is set or the
  // sign-in is turned away; a timer is the safety net.
  const [signingIn, setSigningInState] = useState(false);
  const signingInTimer = useRef(null);
  const setSigningIn = useCallback(on => {
    clearTimeout(signingInTimer.current);
    setSigningInState(on);
    if (on) signingInTimer.current = setTimeout(() => setSigningInState(false), 15000);
  }, []);
  // Error shown on sign-in screens, plus the field to highlight and tone
  // ('error' | 'info'). setError('') clears all three.
  const [error, setErrorText]         = useState('');
  const [errorField, setErrorField]   = useState(null);
  const [errorTone, setErrorTone]     = useState('error');
  const setError = useCallback((message, field = null, tone = 'error') => {
    setErrorText(message || '');
    setErrorField(message ? field : null);
    setErrorTone(message ? tone : 'error');
  }, []);
  const showAuthError = useCallback((e, context) => {
    const d = describeAuthError(e, context);
    setError(d.message, d.field, d.tone);
  }, [setError]);
  const [showWarning, setShowWarning] = useState(false);
  // Sign-in waiting for a 2FA code: { resolver, loginMethod, proof, credential }.
  // proof repeats the first step (password or Google) for the "Lost access
  // to your authenticator app?" options; it only lives in memory.
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [userVersion, setUserVersion]   = useState(0);
  // Bumped when the account in the switcher changes, so the tabs reload
  const [accountVersion, setAccountVersion] = useState(0);
  // View as (Super Admins): who, and what the portal looks like for them
  const [viewAs, setViewAsState]        = useState(viewAsPerson);
  const [viewAsInfo, setViewAsInfo]     = useState(null);
  // 3.69.0: locked after an hour without use ({ email, id, name }); the
  // sign-in page then offers Face ID / fingerprint unlock first
  const [lock, setLock]                 = useState(() => currentLock());
  const [passkeyOffer, setPasskeyOffer] = useState(false);

  // Server said 2FA must be set up, or access was removed
  useEffect(() => {
    async function onAccess(e) {
      const { code, error: message } = e.detail || {};
      const current = auth.currentUser;
      // 3.49.0: this network is blocked (Security → IP Blocking): sign out, say why
      if (code === 'IP_BLOCKED') {
        setSigningIn(false);
        setMfaChallenge(null);
        if (current) await signOut(auth);
        setError(ipBlockedMessage(e.detail));
        return;
      }
      if (!current) return;
      if (code === 'VIEW_AS_INVALID' || code === 'VIEW_AS_DENIED') {
        viewAsPerson = null;
        try { window.sessionStorage.removeItem(VIEW_AS_KEY); } catch { /* ignore */ }
        setViewAsState(null); setViewAsInfo(null);
        return;
      }
      if (code === 'PASSWORD_CHANGE_REQUIRED') {
        setUserInfo(prev => ({ ...prev, mustChangePassword: true }));
        return;
      }
      if (code === 'SIGNED_OUT') {
        // An Admin signed this person out (User Management → Online now)
        await signOut(auth);
        setError(message || 'You were signed out by an administrator. Please sign in again.', null, 'info');
      } else if (code === 'NOT_AUTHORIZED' || code === 'TEMP_PASSWORD_EXPIRED') {
        await signOut(auth);
        setError(message || 'Your account does not have access to the Fax Portal.');
      } else if (code === 'MFA_SETUP_REQUIRED') {
        try {
          await current.reload();
          setUserVersion(v => v + 1);
        } catch {
          await signOut(auth);   // e.g. signed out everywhere after a 2FA reset
        }
      }
    }
    window.addEventListener('fax:access', onAccess);
    return () => window.removeEventListener('fax:access', onAccess);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inactivityTimer  = useRef(null);
  const warningTimer     = useRef(null);
  const countdownTimer   = useRef(null);   // 1-second check while the warning shows
  const heartbeatTimer   = useRef(null);
  const lastActivityRef  = useRef(Date.now());
  const resetInactivityRef = useRef(null);
  // For the countdown pop-up: when the session ends, and "Stay signed in"
  const sessionEndsAt = useCallback(() => lastActivityRef.current + INACTIVITY_TIMEOUT, []);
  const staySignedIn  = useCallback(() => resetInactivityRef.current?.(), []);

  // ── Auth state listener ───────────────────────────────────
  useEffect(() => {
    let wasSignedIn = false;
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      // 3.52.0: signed out (any way, not just opening the page signed out):
      // the app's background helper forgets what it kept
      if (firebaseUser) wasSignedIn = true;
      else if (wasSignedIn) {
        wasSignedIn = false;
        try { navigator.serviceWorker?.controller?.postMessage('clear'); } catch { /* no helper */ }
        try { navigator.clearAppBadge?.().catch(() => {}); } catch { /* not supported */ }   // 3.56.4
        try { window.caches?.keys().then(ks => ks.forEach(k => window.caches.delete(k))); } catch { /* nothing kept */ }
        supportSignOut();   // 3.65.2 (owner): the support site too
      }
      if (firebaseUser) {
        const domain = firebaseUser.email?.split('@')[1] || '';
        if (!ALLOWED_DOMAINS.includes(domain)) {
          signOut(auth);
          setUser(null);
          setUserInfo({ name: '', role: '' });
          setError('Your email domain is not authorized to access this portal.');
        } else {
          // 3.63.0: the portal was closed (or the computer off) for over an
          // hour since it was last used: the inactivity rule still applies,
          // so the password is asked for again. Last used = the newest of
          // the activity this browser shared and the sign-in itself.
          const lastUse = Math.max(sharedLastActivity(), Date.parse(firebaseUser.metadata?.lastSignInTime || '') || 0);
          if (lastUse && Date.now() - lastUse > INACTIVITY_TIMEOUT) {
            // 3.69.0: locked instead, when this device has Face ID / fingerprint unlock
            const locked = lockFor(firebaseUser.email);
            await signOut(auth);
            setLock(locked ? currentLock() : null);
            setError(locked ? '' : IDLE_SIGN_OUT_MESSAGE, null, 'info');
            setSigningIn(false);
            setLoading(false);
            return;
          }
          // Name, role and group from the backend. Accounts without portal
          // access are signed out here with the reason.
          let denied = null;
          let notice = null;   // 3.63.0: signed out for a reason that isn't an error
          try {
            const token = await firebaseUser.getIdToken();
            const res   = await fetch(`${API_BASE}/portal/user-info`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json().catch(() => ({}));
            if (res.status === 403 && (data.code === 'NOT_AUTHORIZED' || data.code === 'TEMP_PASSWORD_EXPIRED')) {
              denied = data.error || 'Your account does not have access to the Fax Portal.';
            } else if (res.status === 403 && data.code === 'SIGNED_OUT') {
              notice = data.error || 'You were signed out by an administrator. Please sign in again.';
            } else if (data.success) {
              setUserInfo(infoFrom(data));
            }
          } catch (e) {
            console.warn('[userInfo] fetch error:', e.message);
            setUserInfo({ name: firebaseUser.displayName || '', role: '' });
          }
          if (denied || notice) {
            await signOut(auth);
            setError(denied || notice, null, denied ? 'error' : 'info');
            setSigningIn(false);
            setLoading(false);
            return;
          }
          setUser(firebaseUser);
          setSigningIn(false);
          setError('');

          // If page was refreshed, create a new session entry
          if (localStorage.getItem('fax_create_session') === 'true') {
            localStorage.removeItem('fax_create_session');
            try {
              const t = await firebaseUser.getIdToken();
              await logSession('login', t, { loginMethod: 'Session Restored' });
            } catch (e) {
              console.warn('[session restore] error:', e.message);
            }
          }
        }
      } else {
        setUser(null);
        setUserInfo({ name: '', role: '' });
        stopTimers();
      }
      setLoading(false);
      if (!firebaseUser) setSigningIn(false);
    });
    return unsub;
  // setError is stable (useCallback with no deps); runs once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Activity tracking ─────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    let lastShared = 0;
    let ending = false;   // inactivity sign-out under way
    // e: an activity event, 'shared' (another tab was used at `at`) or
    // 'stay' ("Stay signed in", always told to the other tabs)
    function resetInactivityTimer(e, at = Date.now()) {
      if (ending) return;
      lastActivityRef.current = at;
      hideWarning();
      // Tell the portal's other tabs (at most every 10 seconds)
      if (e !== 'shared' && (e === 'stay' || at - lastShared > 10000)) {
        lastShared = at;
        try { window.localStorage.setItem(LAST_ACTIVITY_KEY, String(at)); } catch { /* ignore */ }
      }
      const idle = Date.now() - at;

      // Clear existing timers
      if (inactivityTimer.current)  clearTimeout(inactivityTimer.current);
      if (warningTimer.current)     clearTimeout(warningTimer.current);

      // Warning at 25 minutes, sign-out at 30 (both re-check the time and
      // the other tabs first)
      warningTimer.current    = setTimeout(checkIdle, Math.max(0, INACTIVITY_WARNING - idle));
      inactivityTimer.current = setTimeout(checkIdle, Math.max(0, INACTIVITY_TIMEOUT - idle));
    }
    resetInactivityRef.current = () => resetInactivityTimer('stay');

    // Where the session stands by the clock: 'active', 'warning' (last 5
    // minutes) or 'expired'. Activity in another tab counts. Timers can
    // run late (a sleeping computer, a background tab), so this is what
    // decides, not which timer fired.
    function idleState() {
      let idle = Date.now() - lastActivityRef.current;
      if (idle < INACTIVITY_WARNING) return 'active';
      const other = sharedLastActivity();
      if (other > lastActivityRef.current && Date.now() - other < INACTIVITY_TIMEOUT) {
        resetInactivityTimer('shared', other);
        idle = Date.now() - other;
      }
      if (idle >= INACTIVITY_TIMEOUT) return 'expired';
      return idle >= INACTIVITY_WARNING ? 'warning' : 'active';
    }

    // Returns true when the time is up or the warning is (now) showing
    function checkIdle() {
      if (ending) return true;
      const state = idleState();
      if (state === 'expired') { expire(); return true; }
      if (state === 'warning') { showWarningNow(); return true; }
      return false;
    }

    function showWarningNow() {
      setShowWarning(true);
      // Every second while it shows, in case the sign-out timer runs late
      if (!countdownTimer.current) countdownTimer.current = setInterval(checkIdle, 1000);
    }
    function hideWarning() {
      if (countdownTimer.current) { clearInterval(countdownTimer.current); countdownTimer.current = null; }
      setShowWarning(false);
    }

    function expire() {
      if (ending) return;
      ending = true;
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (warningTimer.current)    clearTimeout(warningTimer.current);
      if (countdownTimer.current) { clearInterval(countdownTimer.current); countdownTimer.current = null; }
      // The pop-up (at 0:00, "Signing you out…") covers the page until the
      // sign-out completes
      setShowWarning(true);
      handleInactivityLogout();
    }

    // Activity in this tab. Once the warning shows it no longer counts:
    // only "Stay signed in" (or use of another portal tab) keeps the
    // session, so the pop-up can't vanish on the first mouse move.
    function onActivity(e) {
      if (checkIdle()) return;
      resetInactivityTimer(e);
    }

    // Coming back to the tab (or the computer waking up): sign out now if
    // the time ran out meanwhile, or bring up an overdue warning
    function onReturn() {
      if (document.visibilityState === 'visible') checkIdle();
    }

    // Used in another tab of the portal
    function onStorage(e) {
      if (e.key === LAST_ACTIVITY_KEY && Number(e.newValue) > lastActivityRef.current) resetInactivityTimer('shared', Number(e.newValue));
    }

    activityEvents.forEach(e => window.addEventListener(e, onActivity));
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    resetInactivityTimer(); // Start timers on login

    return () => {
      activityEvents.forEach(e => window.removeEventListener(e, onActivity));
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
      resetInactivityRef.current = null;
      stopTimers();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Heartbeat ─────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    heartbeatTimer.current = setInterval(async () => {
      if (!currentSessionId) return;
      try {
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          await fetch(`${API_BASE}/portal/session/heartbeat`, {
            method : 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body   : JSON.stringify({ sessionId: currentSessionId })
          });
        }
      } catch (e) {
        console.warn('[heartbeat] error:', e.message);
      }
    }, HEARTBEAT_INTERVAL);

    return () => {
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
    };
  }, [user]);

  // ── beforeunload — capture refresh/close ─────────────────
  useEffect(() => {
    if (!user) return;

    function handleUnload() {
      // Use localStorage as fallback since currentSessionId may be lost
      const sessionId = currentSessionId || localStorage.getItem('fax_session_id');
      if (!sessionId) return;
      // sendBeacon doesn't need auth — unload endpoint has no requireAuth
      fetch(`${API_BASE}/portal/session/unload`, {
        method   : 'POST',
        keepalive: true,
        headers  : { 'Content-Type': 'application/json' },
        body     : JSON.stringify({ sessionId })
      });
    }

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [user]);

  // ── Stop all timers ───────────────────────────────────────
  function stopTimers() {
    if (inactivityTimer.current)  clearTimeout(inactivityTimer.current);
    if (warningTimer.current)     clearTimeout(warningTimer.current);
    if (countdownTimer.current) { clearInterval(countdownTimer.current); countdownTimer.current = null; }
    if (heartbeatTimer.current)   clearInterval(heartbeatTimer.current);
    setShowWarning(false);
  }

  // ── Inactivity logout ─────────────────────────────────────
  async function handleInactivityLogout() {
    const locked = lockFor(auth.currentUser?.email);   // 3.69.0
    try {
      if (currentSessionId) {
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          await fetch(`${API_BASE}/portal/session/logout`, {
            method : 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body   : JSON.stringify({ sessionId: currentSessionId, reason: 'inactivity' })
          });
          currentSessionId = null;
          localStorage.removeItem('fax_session_id');
        }
      }
      stopTimers();
      await signOut(auth);
    } catch (e) {
      console.warn('[inactivity logout] error:', e.message);
      await signOut(auth);
    }
    // 3.63.0: say why on the sign-in page (3.69.0: or show the lock screen)
    setLock(locked ? currentLock() : null);
    setError(locked ? '' : IDLE_SIGN_OUT_MESSAGE, null, 'info');
  }

  // ── Session logging ───────────────────────────────────────
  async function logSession(action, token, extra = {}) {
    try {
      if (action === 'login') {
        const res  = await fetch(`${API_BASE}/portal/session/login`, {
          method : 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body   : JSON.stringify(extra)
        });
        const data = await res.json();
        if (data.sessionId) {
          currentSessionId = data.sessionId;
          localStorage.setItem('fax_session_id', data.sessionId);
        }
      } else if (action === 'logout' && currentSessionId) {
        await fetch(`${API_BASE}/portal/session/logout`, {
          method : 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body   : JSON.stringify({ sessionId: currentSessionId, ...extra })
        });
        currentSessionId = null;
        localStorage.removeItem('fax_session_id');
      }
    } catch (e) {
      console.warn('[session]', action, 'error:', e.message);
    }
  }

  // ── Login with Google (UNCHANGED) ────────────────────────
  async function loginWithGoogle() {
    setError('');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const domain = result.user.email?.split('@')[1] || '';
      if (!ALLOWED_DOMAINS.includes(domain)) {
        await signOut(auth);
        setError('Your Google account domain is not authorized.');
        return false;
      }
      if (!user) setSigningIn(true);
      fullSignInDone();
      const token = await result.user.getIdToken();
      await logSession('login', token, { loginMethod: 'Google' });
      return true;
    } catch (e) {
      if (e.code === 'auth/multi-factor-auth-required') {
        const credential = googleCredentialFromMfaError(e);
        if (credential?.idToken && await signInOnRememberedDevice(emailFromIdToken(credential.idToken),
          { method: 'google', idToken: credential.idToken }, 'Google')) return true;
        setMfaChallenge({
          resolver: getMultiFactorResolver(auth, e), loginMethod: 'Google', credential,
          proof: credential?.idToken ? { method: 'google', idToken: credential.idToken }
            : credential?.accessToken ? { method: 'google', accessToken: credential.accessToken } : null,
        });
        return 'mfa';
      }
      showAuthError(e);
      return false;
    }
  }

  // ── Sign in with Microsoft (3.72.0, owner, Oct 30) ─────────
  // Like Google (3.72.1, owner): a Microsoft account with the same email as
  // a portal account is joined to it by Firebase here; one with another
  // email works once connected in Security. Anything else makes no new
  // sign-in: Firebase makes a stray Microsoft-only account, deleted at once
  // here (and the server refuses it anyway). The server also wants the 2FA
  // code entered in every Microsoft sign-in, so "Don't ask for a code on
  // this device" isn't offered, and the lost-authenticator options need the
  // password or Google sign-in.
  async function loginWithMicrosoft() {
    setError('');
    try {
      const result = await signInWithPopup(auth, microsoftProvider);
      const ids = result.user.providerData.map(p => p.providerId);
      if (!ids.includes('password') && !ids.includes('google.com')) {
        await result.user.delete().catch(() => signOut(auth));
        setError(MS_NOT_CONNECTED);
        return false;
      }
      if (!user) setSigningIn(true);
      fullSignInDone();
      const token = await result.user.getIdToken();
      await logSession('login', token, { loginMethod: 'Microsoft' });
      return true;
    } catch (e) {
      if (e.code === 'auth/multi-factor-auth-required') {
        setMfaChallenge({ resolver: getMultiFactorResolver(auth, e), loginMethod: 'Microsoft', credential: null, proof: null });
        return 'mfa';
      }
      if (e.code === 'auth/account-exists-with-different-credential') { setError(MS_NOT_CONNECTED); return false; }
      if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request') {
        setError('The Microsoft sign-in window was closed before you finished. Click "Continue with Microsoft" to try again.', null, 'info');
        return false;
      }
      if (e.code === 'auth/popup-blocked') { setError('Your browser blocked the Microsoft sign-in window. Allow pop-ups for fax.crazypixels.org, then click "Continue with Microsoft" again.'); return false; }
      showAuthError(e);
      return false;
    }
  }

  // Connect / disconnect Microsoft (Security → Password, 3.72.0). Firebase
  // may want a recent sign-in first: auth/requires-recent-login. With 2FA on,
  // Firebase also asks for the authenticator code before it links (3.72.1):
  // returned as { mfa: resolver }, then finishMicrosoftConnect(resolver, code).
  async function connectMicrosoft() {
    try {
      await linkWithPopup(user, microsoftProvider);
    } catch (e) {
      if (e.code === 'auth/multi-factor-auth-required') return { mfa: getMultiFactorResolver(auth, e) };
      throw e;
    }
    await microsoftConnected();
    return { ok: true };
  }
  async function finishMicrosoftConnect(resolver, code) {
    const hint = resolver.hints.find(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, String(code).trim()));
    await microsoftConnected();
  }
  async function microsoftConnected() {
    await user.reload();
    setUserVersion(v => v + 1);
    const token = await user.getIdToken(true);
    fetch(`${API_BASE}/portal/me/sign-in-method`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider: 'microsoft', action: 'connected' }) }).catch(() => {});
  }
  async function disconnectMicrosoft() {
    await unlink(user, 'microsoft.com');
    await user.reload();
    setUserVersion(v => v + 1);
    const token = await user.getIdToken(true);
    fetch(`${API_BASE}/portal/me/sign-in-method`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider: 'microsoft', action: 'disconnected' }) }).catch(() => {});
  }

  // ── Login with Email (UNCHANGED) ─────────────────────────
  async function loginWithEmail(typedEmail, password) {
    setError('');
    // A secondary email signs in to their account (its main email)
    let email = String(typedEmail || '').trim();
    try { email = (await signInEmailFor(email)).email || email; } catch (e) {
      if (e.code === 'IP_BLOCKED') { setError(ipBlockedMessage(e.block)); return false; }   // 3.49.0: don't even try
      /* otherwise use what was typed */
    }
    const domain = email.split('@')[1] || '';
    if (!ALLOWED_DOMAINS.includes(domain)) {
      setError('This email address can\'t sign in to the portal. Please use your work email.', 'email');
      return false;
    }
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (!user) setSigningIn(true);
      fullSignInDone();
      const token  = await result.user.getIdToken();
      await logSession('login', token, { loginMethod: 'Email/Password' });
      return true;
    } catch (e) {
      if (e.code === 'auth/multi-factor-auth-required') {
        if (await signInOnRememberedDevice(email, { method: 'password', email, password }, 'Email/Password')) return true;
        setMfaChallenge({
          resolver: getMultiFactorResolver(auth, e), loginMethod: 'Email/Password',
          proof: { method: 'password', email, password },
        });
        return 'mfa';
      }
      showAuthError(e);
      const r = await reportSignInFailure(email, e);   // 3.49.0
      if (r?.blocked) setError(ipBlockedMessage(r));
      return false;
    }
  }

  // ── Forgot Password (UNCHANGED) ──────────────────────────
  // The server checks the address first (work email, portal account,
  // Google-only account) and explains what's wrong
  async function forgotPassword(email) {
    setError('');
    try {
      const res  = await fetch(`${API_BASE}/portal/forgot-password`, {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ email })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "We couldn't send the reset email. Please try again.", 'email', data.code === 'GOOGLE_ONLY' ? 'info' : 'error');
        return false;
      }
      return true;
    } catch (e) {
      showAuthError(e);
      return false;
    }
  }

  // ── Change Password (UNCHANGED) ──────────────────────────
  // New password in My profile: changes it, or adds one to a Google-only
  // account (then they can sign in either way). Firebase asks for a recent
  // sign-in: then this throws auth/requires-recent-login and the page asks
  // them to confirm it's them (password or Google, plus the 2FA code).
  async function setOwnPassword(newPassword) {
    const u = auth.currentUser;
    if (signInMethods(u).password) await updatePassword(u, newPassword);
    else await linkWithCredential(u, EmailAuthProvider.credential(u.email, newPassword));
    await u.reload();
    setUserVersion(v => v + 1);
  }

  async function changePassword(currentPassword, newPassword) {
    setError('');
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      return true;
    } catch (e) {
      showAuthError(e, 'reauth');
      return false;
    }
  }

  // ── Logout (UNCHANGED logic, added stopTimers) ────────────
  async function logout() {
    // 3.69.2 (owner): on a device with Face ID / fingerprint on, the
    // sign-in page then offers it ("Sign in with Touch ID"); 3.69.0 asked
    // for the password after signing out yourself
    const locked = lockFor(auth.currentUser?.email, 'signout');
    setLock(locked ? currentLock() : null);
    try {
      stopTimers();
      if (currentSessionId) {
        const token = await auth.currentUser?.getIdToken();
        if (token) await logSession('logout', token, { reason: 'sign_out' });
      }
      await signOut(auth);
    } catch (e) {
      showAuthError(e);
    }
  }

  // ── Two-factor authentication (authenticator app, TOTP) ─────
  // Needs "Firebase Authentication with Identity Platform" and the TOTP
  // second factor enabled on the project; until then Firebase answers
  // enrollment with auth/operation-not-allowed.
  const TOTP = TotpMultiFactorGenerator.FACTOR_ID;

  // The password (or Google) step passed on a remembered device: sign in
  // without the code. False when the device isn't remembered (any more),
  // and the code screen shows as usual.
  async function signInOnRememberedDevice(email, proof, loginMethod) {
    const device = rememberedDeviceFor(email);
    if (!device) return false;
    try {
      if (!user) setSigningIn(true);
      const { customToken } = await rememberedSignIn(proof, device.token);
      const result = await signInWithCustomToken(auth, customToken);
      fullSignInDone();
      const token  = await result.user.getIdToken();
      await logSession('login', token, { loginMethod: `${loginMethod} + remembered device` });
      return true;
    } catch (e) {
      setSigningIn(false);
      if (e.code === 'DEVICE_NOT_TRUSTED') forgetDeviceLocally(email);
      return false;
    }
  }

  // Finish a sign-in that asked for a 2FA code. remember: "Don't ask for a
  // code on this device for 30 days" (a failure there doesn't stop sign-in).
  async function completeMfaSignIn(code, remember = false) {
    setError('');
    const challenge = mfaChallenge;
    if (!challenge) return false;
    try {
      const hint = challenge.resolver.hints.find(h => h.factorId === TOTP);
      if (!hint) throw Object.assign(new Error('No authenticator app is set up for this account.'), { code: 'no-totp' });
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, String(code).trim());
      const result    = await challenge.resolver.resolveSignIn(assertion);
      const domain    = result.user.email?.split('@')[1] || '';
      if (!ALLOWED_DOMAINS.includes(domain)) {
        await signOut(auth);
        setMfaChallenge(null);
        setError('Your email domain is not authorized.');
        return false;
      }
      // Keep the loading screen up until the portal is ready (no flash of
      // the empty sign-in form)
      if (!user) setSigningIn(true);
      setMfaChallenge(null);
      fullSignInDone();
      const token = await result.user.getIdToken();
      await logSession('login', token, { loginMethod: `${challenge.loginMethod} + 2FA` });
      if (remember) {
        try {
          const r = await rememberThisDevice(deviceLabel(), () => result.user.getIdToken());
          const map = readDevices();
          map[String(result.user.email || '').toLowerCase()] = { id: r.id, token: r.token, expiresAt: r.expiresAt };
          writeDevices(map);
        } catch (e2) { console.warn('[2fa] could not remember this device:', e2.message); }
      }
      return true;
    } catch (e) {
      showAuthError(e);
      const r = await reportSignInFailure(challenge.proof?.email || '', e);   // 3.49.0: wrong codes count too
      if (r?.blocked) { setMfaChallenge(null); setError(ipBlockedMessage(r)); }
      return false;
    }
  }

  // ── Face ID / fingerprint unlock (3.69.0) ─────────────────
  // The lock screen's button. Problems the server reports (30 days up, an
  // Admin signed them out, password changed…) leave the lock for the
  // password form with the reason; a cancelled Face ID stays on the lock.
  // quiet: started by itself (the app), so a refusal before any tap says nothing
  async function unlockWithPasskey({ quiet = false } = {}) {
    const l = lock || currentLock();
    if (!l) { setLock(null); return false; }
    setError('');
    try {
      const customToken = await unlockToken(l);
      setSigningIn(true);
      // the hour starts again now (the reopen check reads this)
      try { window.localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch { /* the sign-in time counts too */ }
      const result = await signInWithCustomToken(auth, customToken);
      clearLock(); setLock(null);
      const token = await result.user.getIdToken();
      await logSession('login', token, { loginMethod: l.why === 'signout' ? 'Device sign-in' : 'Device unlock' });
      return true;
    } catch (e) {
      setSigningIn(false);
      if (e.code === 'CANCELLED' || e.code === 'DEVICE' || e.code === 'NOT_VERIFIED' || e.code === 'CHALLENGE_EXPIRED') { if (!(quiet && e.code === 'CANCELLED')) setError(e.message, null, 'info'); return false; }
      if (e.code === 'PASSKEY_GONE') forgetPasskey(l.email);
      if (e.code === 'IP_BLOCKED') { leaveLock(); setError(ipBlockedMessage(e)); return false; }
      leaveLock();
      setError(e.message || 'Please sign in with your password.', null, 'info');
      return false;
    }
  }
  // Locked or unlocked in another tab of the portal
  useEffect(() => {
    const onStorage = e => { if (e.key === LOCK_KEY || e.key === null) setLock(currentLock()); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  // 3.74.4 (owner): "Sign in with a passkey" on the sign-in page, on any
  // device and without the 6-digit code (the passkey is two proofs). This
  // device then keeps it, so the hourly lock and "Welcome back" work here too.
  async function loginWithPasskey() {
    setError('');
    try {
      const r = await passkeySignInToken();
      setSigningIn(true);
      try { window.localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch { /* the sign-in time counts too */ }
      const result = await signInWithCustomToken(auth, r.customToken);
      clearLock(); setLock(null);
      savePasskey(r.email, { id: r.id, name: (result.user.displayName || '').split(' ')[0], validUntil: r.validUntil, paused: false });
      const token = await result.user.getIdToken();
      await logSession('login', token, { loginMethod: 'Passkey' });
      return true;
    } catch (e) {
      setSigningIn(false);
      if (e.code === 'IP_BLOCKED') { setError(ipBlockedMessage(e)); return false; }
      setError(e.message || 'Please sign in with your password.', null, e.code === 'CANCELLED' || e.code === 'NOT_VERIFIED' ? 'info' : undefined);
      return false;
    }
  }
  // "Use password instead": the usual sign-in form (Face ID stays set up)
  function leaveLock() { clearLock(); setLock(null); setError(''); }
  // 3.69.2: an email typed on the sign-in page that has it on this device
  async function deviceSignIn(email) {
    if (!lockFor(email, 'signout')) return false;
    setLock(currentLock());
    return unlockWithPasskey();
  }

  // Right after a sign-in with the password: renew this device's passkey,
  // or (a face or fingerprint reader, not "Not now" lately) ask whether to
  // turn it on. 3.69.1 (owner): computers are asked too, not only the app.
  useEffect(() => {
    if (!user || !fullSignInAt || Date.now() - fullSignInAt > 10 * 60 * 1000) return;
    fullSignInAt = 0;
    const email = user.email;
    if (passkeyFor(email)) { renewPasskey(email, () => user.getIdToken()); return; }
    if (offerDeclined(email)) return;
    passkeysPossible().then(ok => { if (ok && auth.currentUser?.email === email) setPasskeyOffer(true); });
  }, [user]);

  function cancelMfaSignIn() {
    setMfaChallenge(null);
    setError('');
  }

  // ── Lost access to the authenticator app ──────────────────
  function challengeProof() {
    const proof = mfaChallenge?.proof;
    if (!proof) throw new Error(mfaChallenge?.loginMethod === 'Google'
      ? "We couldn't confirm your Google sign-in. Go back and choose Continue with Google again, or sign in with your email and password."
      : mfaChallenge?.loginMethod === 'Microsoft'
        ? 'To use these options, go back and sign in with your email and password (or Google).'
        : 'Please go back and sign in again.');
    return proof;
  }

  // A recovery code removes the old authenticator. Then sign in again the
  // same way (now without a code) and the app asks for a new authenticator.
  // Returns 'signed-in', or 'sign-in-again' if that last step needs the user.
  async function recoverWithCode(code) {
    const challenge = mfaChallenge;
    await lostUseRecoveryCode(challengeProof(), code);
    try {
      const result = challenge.proof.method === 'password'
        ? await signInWithEmailAndPassword(auth, challenge.proof.email, challenge.proof.password)
        : await signInWithCredential(auth, challenge.credential);
      setMfaChallenge(null);
      fullSignInDone();
      const token = await result.user.getIdToken();
      await logSession('login', token, { loginMethod: `${challenge.loginMethod} + recovery code` });
      return 'signed-in';
    } catch {
      setMfaChallenge(null);
      return 'sign-in-again';
    }
  }

  async function emailTwoFactorResetLink() {
    return lostEmailResetLink(challengeProof());
  }

  // Who can reset it for them, then ask the ones they pick (approver ids)
  async function listTwoFactorApprovers() {
    return lostApprovers(challengeProof());
  }

  async function askAdminForTwoFactorReset(to) {
    return lostAskAdmin(challengeProof(), to);
  }

  function isTotpEnabled(u = user) {
    return !!u && multiFactor(u).enrolledFactors.some(f => f.factorId === TOTP);
  }

  // Step 1 of turning 2FA on: a new secret to scan into the app
  async function startTotpEnrollment() {
    const session = await multiFactor(user).getSession();
    const secret  = await TotpMultiFactorGenerator.generateSecret(session);
    return {
      secret,
      key   : secret.secretKey,
      qrUrl : secret.generateQrCodeUrl(user.email, 'Crazy Pixels Fax Portal'),
    };
  }

  // Step 2: confirm with a code from the app
  async function finishTotpEnrollment(secret, code) {
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, String(code).trim());
    await multiFactor(user).enroll(assertion, 'Authenticator app');
    await user.reload();
    // reload() updates the same user object: bump the counter so the app
    // re-renders and sees the new authenticator (needsTwoFactor)
    setUserVersion(v => v + 1);
  }

  // Removes the authenticator; the app then asks for a new one right away
  async function disableTotp() {
    const factor = multiFactor(user).enrolledFactors.find(f => f.factorId === TOTP);
    if (factor) await multiFactor(user).unenroll(factor);
    await user.reload().catch(() => {});
    setUserVersion(v => v + 1);
  }

  // Some changes need a recent sign-in. Password accounts confirm with
  // their password; Google accounts with a Google pop-up. If 2FA is on,
  // Firebase asks for a code too — returned as { mfa: resolver }.
  // Confirm it's you: with the password when one is given, otherwise Google
  async function reauthenticate(password) {
    try {
      if (password) {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
      } else {
        await reauthenticateWithPopup(user, googleProvider);
      }
      return { ok: true };
    } catch (e) {
      if (e.code === 'auth/multi-factor-auth-required') return { mfa: getMultiFactorResolver(auth, e) };
      throw e;
    }
  }

  async function resolveReauthWithCode(resolver, code) {
    const hint = resolver.hints.find(h => h.factorId === TOTP);
    await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, String(code).trim()));
  }

  // Our backend sends the verification email (our branding and domain)
  // instead of Firebase's template. Returns { alreadyVerified } when the
  // address is already verified.
  async function sendVerificationEmail() {
    const token = await user.getIdToken();
    const res   = await fetch(`${API_BASE}/portal/me/send-verification`, {
      method : 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not send the verification email.');
    return data;
  }

  // Re-read the account from Firebase (e.g. after verifying the email).
  // reload() updates the same user object, so bump a counter to re-render.
  async function refreshUser() {
    await user.reload();
    setUserVersion(v => v + 1);
  }

  // ── Account switcher ────────────────────────────────────────
  const accountBeforeViewAs = useRef(readAccount());
  // Pick an account ('all' for every account, Super Admins). Your role
  // may differ per account, so your details are read again.
  const switchAccount = useCallback(async id => {
    setCurrentAccount(id);
    const token = await auth.currentUser?.getIdToken();
    const headers = { Authorization: `Bearer ${token}` };
    if (viewAsPerson) headers['X-View-As'] = viewAsPerson.email;
    const res = await fetch(`${API_BASE}/portal/user-info`, { headers });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.success) {
      if (viewAsPerson) setViewAsInfo(infoFrom(data)); else setUserInfo(infoFrom(data));
    }
    setAccountVersion(v => v + 1);
  }, []);

  // Read your details again without reloading the tabs (3.19.6): after a
  // team change, and when the portal comes back into view (at most once a
  // minute), so a new team shows up in the Team filters and columns
  const lastInfoAt = useRef(0);
  const refreshInfo = useCallback(async (force = false) => {
    if (!auth.currentUser) return;
    if (!force && Date.now() - lastInfoAt.current < 60000) return;
    lastInfoAt.current = Date.now();
    try {
      const token = await auth.currentUser.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };
      if (viewAsPerson) headers['X-View-As'] = viewAsPerson.email;
      const res = await fetch(`${API_BASE}/portal/user-info`, { headers });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) { if (viewAsPerson) setViewAsInfo(infoFrom(data)); else setUserInfo(infoFrom(data)); }
    } catch { /* keep what we have */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAsPerson]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refreshInfo(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => { document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); };
  }, [refreshInfo]);

  // ── View as (Super Admins, full access; changes are logged) ─
  // What the portal looks like for the person being viewed
  const loadViewAsInfo = useCallback(async person => {
    const token = await auth.currentUser?.getIdToken();
    const res = await fetch(`${API_BASE}/portal/user-info`, {
      headers: { Authorization: `Bearer ${token}`, 'X-View-As': person.email },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) throw new Error(data.error || "Couldn't view the portal as this person.");
    return infoFrom(data);
  }, []);

  async function startViewAs(email) {
    const token = await auth.currentUser.getIdToken();
    const res = await fetch(`${API_BASE}/portal/users/view-as`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Couldn't start View as.");
    // Super Admins: their own main account first; yours comes back on exit.
    // Read-only View as (Admins) stays in your own account.
    accountBeforeViewAs.current = currentAccount;
    if (!data.person.readOnly) setCurrentAccount(null);
    const info = await loadViewAsInfo(data.person);
    viewAsPerson = data.person;
    try { window.sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(data.person)); } catch { /* ignore */ }
    setViewAsInfo(info);
    setViewAsState(data.person);
    // Reload every tab as this person (nothing loaded as you stays on screen)
    setAccountVersion(v => v + 1);
  }

  async function exitViewAs() {
    const person = viewAsPerson;
    viewAsPerson = null;
    // Back to the account you were looking at before
    setCurrentAccount(accountBeforeViewAs.current);
    setAccountVersion(v => v + 1);
    try { window.sessionStorage.removeItem(VIEW_AS_KEY); } catch { /* ignore */ }
    setViewAsState(null);
    setViewAsInfo(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (token && person) {
        await fetch(`${API_BASE}/portal/users/view-as/end`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: person.email }),
        });
      }
    } catch { /* only the log entry is missed */ }
  }

  // After a page reload while viewing: load that person's view again
  useEffect(() => {
    if (!user || !viewAs || viewAsInfo) return;
    loadViewAsInfo(viewAs).then(setViewAsInfo).catch(() => { exitViewAs(); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, viewAs]);

  // Signing out forgets the picked account
  useEffect(() => {
    if (!user && !loading && currentAccount) { setCurrentAccount(null); accountBeforeViewAs.current = null; }
  }, [user, loading]);

  // Signing out ends View as
  useEffect(() => {
    if (!user && viewAsPerson) {
      viewAsPerson = null;
      try { window.sessionStorage.removeItem(VIEW_AS_KEY); } catch { /* ignore */ }
      setViewAsState(null); setViewAsInfo(null);
    }
  }, [user]);

  // What the portal shows: your own role, or the viewed person's
  const effectiveInfo = useMemo(
    () => (viewAs ? (viewAsInfo || { ...userInfo, perms: {} }) : userInfo),
    [viewAs, viewAsInfo, userInfo]);
  const can = useCallback(key => !!effectiveInfo?.perms?.[key], [effectiveInfo]);
  // Same as can (kept for components that show a button by permission)
  // Buttons for changes are hidden during read-only View as (Admins); the
  // server refuses them too
  const shows = useCallback(key => !!effectiveInfo?.perms?.[key] && !(viewAs?.readOnly && WRITE_PERMS.includes(key)), [effectiveInfo, viewAs]);

  // ── Temporary password → their own ────────────────────────
  // currentPassword is only needed if Firebase asks for a recent sign-in
  async function replaceTemporaryPassword(newPassword, currentPassword) {
    try {
      await updatePassword(user, newPassword);
    } catch (e) {
      if (e.code !== 'auth/requires-recent-login' || !currentPassword) throw e;
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, currentPassword));
      await updatePassword(user, newPassword);
    }
    const token = await user.getIdToken(true);
    const res = await fetch(`${API_BASE}/portal/me/password-changed`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
    setUserInfo(prev => ({ ...prev, mustChangePassword: false }));
  }

  // Merge saved profile changes into what the header shows
  const updateUserInfo = useCallback(changes => setUserInfo(prev => ({ ...prev, ...changes })), []);

  // Stable identity (changes only with the signed-in user) so hooks that
  // depend on it — live status polling, search suggestions — don't restart
  // every time this provider re-renders
  const getToken = useCallback(async () => (user ? user.getIdToken() : null), [user]);

  // Two-factor authentication is required: signed in without an
  // authenticator → the app shows the setup screen instead of the portal.
  // userVersion is bumped whenever the account is re-read.
  const needsTwoFactor = !!user && !isTotpEnabled(user) && userVersion >= 0;

  return (
    <AuthContext.Provider value={{
      user, userInfo, loading, error, setError, errorField, errorTone, showWarning, sessionEndsAt, staySignedIn,
      loginWithGoogle, loginWithMicrosoft, connectMicrosoft, finishMicrosoftConnect, disconnectMicrosoft, loginWithEmail, forgotPassword,
      changePassword, logout, getToken, updateUserInfo,
      mfaChallenge, completeMfaSignIn, cancelMfaSignIn, signingIn,
      recoverWithCode, emailTwoFactorResetLink, listTwoFactorApprovers, askAdminForTwoFactorReset, needsTwoFactor,
      isTotpEnabled, startTotpEnrollment, finishTotpEnrollment, disableTotp,
      reauthenticate, resolveReauthWithCode, sendVerificationEmail, refreshUser, userVersion, setOwnPassword, refreshInfo,
      viewAs, viewAsReady: !viewAs || !!viewAsInfo, startViewAs, exitViewAs, effectiveInfo, can, shows,
      switchAccount, accountVersion,
      needsPasswordChange: !!user && !!userInfo?.mustChangePassword, replaceTemporaryPassword,
      lock, unlockWithPasskey, leaveLock, deviceSignIn, loginWithPasskey, passkeyOffer, endPasskeyOffer: () => setPasskeyOffer(false),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}